<script setup>
import {useRoute, useRouter} from "vue-router";
import {computed, onMounted, reactive, ref, getCurrentInstance} from "vue";
import {
  accessControlApi,
  articlesApi,
  connectionsApi,
  futured_medias_Api,
  prompt_templates_Api,
  sample_articles_Api
} from "@/api/forwriting_index";
import CheckPointLimit from "@/views/forwriting/fw00_check_point_limit.vue"
import LoadingIcon from "@/components/LoadingIcon.vue";

const router = useRouter()
const route = useRoute()
const article_list_id = route.params.article_list_id;
const article_id = route.params.article_id;

// for locale
const instance = getCurrentInstance()
const t = instance.proxy.$t

console.log( "** article_list_id:", article_list_id)
console.log( "** article_id:", article_id)

// for modal check
const modal_check = reactive({
  "show": false,
  "msg": "modal_check"
})
function is_empty( val) {
  if( val == null) return true
  if( typeof val === "string" && val.trim().length === 0) return true
  return false
}
function has_empty_fields() {
  console.log( "generate_type_id:", data.article.generate_type_id)

  let f_list = ""

  const generate_type_id = parseInt( data.article.generate_type_id)
  if( generate_type_id === 3 || generate_type_id === 4) {
    if( is_empty( data.article.urls))
      f_list += t('Articles.urls') + ", "
  }

  if( generate_type_id === 6) {
    if( is_empty( data.article.additional_prompt))
      f_list += t('Articles.agent_instruction') + ", "
  } else {
    if( is_empty( data.article.sample_article_id))
      f_list += t('Articles.sample_article_name') + ", "
    if( is_empty( data.article.prompt_template_id))
      f_list += t('Articles.prompt_template_name') + ", "
  }
  // connection_id 可以為 null 或 0（選擇「無」時只保存不發布），所以不需要驗証

  // has some empty fields...
  if( f_list.length > 0) {
    modal_check.msg = f_list.substring( 0, f_list.length-2)
        + t( 'Articles.check_modal_msg')
    return true
  }

  return false
}
const generate_type_changed = async( ) => {
  console.log( "generate_type_id:", data.article.generate_type_id)
  const generate_type_id = parseInt(data.article.generate_type_id)
  if (generate_type_id === 4) {
    data.article.image_generation_mode = false
    data.article.image_style_setting = ""
  }
  // 音声モードでは画像生成無効
  if (generate_type_id === 5) {
    data.article.image_generation_mode = false
    data.article.image_style_setting = ""
  }
}
const open_check_modal = () => {
  let has_empty = has_empty_fields()
  if( has_empty) {
    modal_check.show = true
  }
  return has_empty
}

const data = reactive({
  article: {
    "posted_url": "",
    "article_title": "",

    "keyword": "",
    "subtitles": "",
    "urls": null,

    "slug": "",
    "tags": "",
    "content": "",

    "article_generate_status": 0,
    "cms_post_status": 0,

    "article_list_id": null,
    "generate_type_id": 3,

    "sample_article_id": null,
    "prompt_template_id": null,
    "futured_media_id": null,
    "connection_id": null,
    "ai_model_id": 1,  // 預設為 Claude Sonnet 4.5
    "image_generation_mode": false,
    "image_style_setting": ""
  },

  editorData: "",

  sample_articles: {},
  sample_articles_content: {},

  prompt_templates: {},
  prompt_templates_content: {},

  generate_types: {
    1: '長文生成(キーワードから)',     // 保留以支持舊文章顯示
    2: '長文生成(URL & 見出しから)',  // 保留以支持舊文章顯示
    3: '元ネタから生成(URLから)',  // 預設值，確保立即顯示
    4: '元ネタから生成(画像転載あり)'  // 画像転載モード
  },
  futured_media: {},
  futured_medias: {},
  connections: {},
  connections_data: {}, // 存儲連接的完整信息（包括 integration）
  ai_models: {},

  tags: []
})

const normalizeFuturedMediaId = (value) => {
  if (value === null || value === undefined || value === "" || value === 0 || value === "0") {
    return 0
  }
  if (value === -1 || value === "-1") {
    return -1
  }
  const parsed = parseInt(value, 10)
  return Number.isNaN(parsed) ? 0 : parsed
}

// URL/テキスト 自動判定（冒頭が http:// / https:// で始まる場合のみURLモード）
const isUrlMode = computed(() => {
  const v = (data.article.urls || '').trim()
  if (!v) return true  // 空欄はURLモードをデフォルト表示
  return /^https?:\/\//i.test(v)
})

// 一次ソース判定
const primarySourceCheck = reactive({
  loading: false,
  results: [],
  checked: false
})

const check_primary_source = async () => {
  if (!data.article.urls || !data.article.urls.trim()) {
    alert(t('Articles_Edit.primary_source_no_urls'))
    return
  }
  primarySourceCheck.loading = true
  primarySourceCheck.results = []
  primarySourceCheck.checked = false
  try {
    const resp = await articlesApi.check_primary_source(data.article.urls)
    if (resp.status === 200 && resp.data && resp.data.results) {
      primarySourceCheck.results = resp.data.results
      primarySourceCheck.checked = true
    }
  } catch (error) {
    console.error("Primary source check error:", error)
    alert(t('Articles_Edit.primary_source_error'))
  } finally {
    primarySourceCheck.loading = false
  }
}

const get_futured_media = async( ) => {
  // 如果 futured_media_id 是 -1（AI Generation）、0 或 null，清除圖片預覽
  if (!data.article.futured_media_id || data.article.futured_media_id === -1 || data.article.futured_media_id === 0 || data.article.futured_media_id === "-1") {
    data.futured_media = {}
    return
  }
  
  // 只有當 futured_media_id 是有效的正整數時才調用 API
  const mediaId = parseInt(data.article.futured_media_id)
  if (mediaId > 0) {
    try {
      const resp_4 = await futured_medias_Api.get_1(mediaId)
      data.futured_media = resp_4.data
    } catch (error) {
      console.error("獲取 futured_media 失敗:", error)
      data.futured_media = {}
    }
  } else {
    data.futured_media = {}
  }
}

// 檢查選中的連接是否為 WordPress
const isWordPressConnection = computed(() => {
  const connectionId = data.article.connection_id
  if (!connectionId || connectionId === 0 || connectionId === null) {
    return false
  }
  const connection = data.connections_data[connectionId]
  return connection && connection.integration === 'wordpress'
})

const _langEntries = [
  { label: "繁體中文", keywords: ["繁體中文", "繁体中文", "Traditional Chinese", "台灣中文", "台灣繁體", "中文", "華語", "華文", "中国語", "tiếng Trung"] },
  { label: "簡體中文", keywords: ["簡體中文", "简体中文", "Simplified Chinese"] },
  { label: "日本語",   keywords: ["日本語", "Japanese", "日語", "日文", "tiếng Nhật", "일본어", "ภาษาญี่ปุ่น"] },
  { label: "한국어",   keywords: ["한국어", "韓国語", "Korean", "韓語", "韓文", "朝鮮語", "조선어", "tiếng Hàn"] },
  { label: "English",  keywords: ["English", "英語", "英文", "英国語", "tiếng Anh", "영어", "ภาษาอังกฤษ"] },
  { label: "ภาษาไทย", keywords: ["ภาษาไทย", "Thai", "泰語", "泰文", "タイ語", "태국어", "tiếng Thái"] },
  { label: "Tiếng Việt", keywords: ["Tiếng Việt", "Vietnamese", "越南語", "越南文", "ベトナム語", "베트남어"] },
  { label: "Español",  keywords: ["Español", "Spanish", "西班牙語", "西班牙文", "スペイン語", "tiếng Tây Ban Nha"] },
  { label: "Français", keywords: ["Français", "French", "法語", "法文", "フランス語", "tiếng Pháp"] },
  { label: "Deutsch",  keywords: ["Deutsch", "German", "德語", "德文", "ドイツ語", "tiếng Đức"] },
]

function _escRe(s) { return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') }

const _targetPatterns = [
  kw => new RegExp(`使用\\s*${_escRe(kw)}`),
  kw => new RegExp(`用\\s*${_escRe(kw)}\\s*[來来]`),
  kw => new RegExp(`以\\s*${_escRe(kw)}\\s*[來来撰書写]`),
  kw => new RegExp(`${_escRe(kw)}\\s*[來来]\\s*撰寫`),
  kw => new RegExp(`絕對要使用\\s*${_escRe(kw)}`),
  kw => new RegExp(`(?:改寫|改写|翻譯|翻译|轉換|转换|轉成|转成)\\s*(?:為|为|成)\\s*${_escRe(kw)}`),
  kw => new RegExp(`(?:寫成|写成)\\s*${_escRe(kw)}`),
  kw => new RegExp(`(?:請|请)\\s*(?:改寫|改写|翻譯|翻译)\\s*(?:為|为|成)?\\s*${_escRe(kw)}`),
  kw => new RegExp(`(?:轉為|转为|換成|换成|改成|譯為|译为|譯成|译成|轉譯為|转译为)\\s*${_escRe(kw)}`),
  kw => new RegExp(`用\\s*${_escRe(kw)}\\s*(?:寫|写|書寫|书写|輸出|输出|產出|产出|生成|編寫|编写|整理|摘要)`),
  kw => new RegExp(`以\\s*${_escRe(kw)}\\s*(?:書寫|书写|輸出|输出|產出|产出|生成|編寫|编写)`),
  kw => new RegExp(`(?:改用|換用|换用)\\s*${_escRe(kw)}`),
  kw => new RegExp(`(?:重寫|重写|重新撰寫|重新撰写|重新編寫|重新编写)\\s*(?:為|为|成)?\\s*${_escRe(kw)}`),
  kw => new RegExp(`(?:撰寫|撰写|編寫|编写)\\s*(?:成|為|为)\\s*${_escRe(kw)}`),
  kw => new RegExp(`${_escRe(kw)}\\s*で(?:書|執筆|記述|リライト|生成|要約|まとめ)`),
  kw => new RegExp(`${_escRe(kw)}\\s*を用いて`),
  kw => new RegExp(`${_escRe(kw)}\\s*で(?:の|作成|出力|回答)`),
  kw => new RegExp(`${_escRe(kw)}\\s*に\\s*(?:書き換|書き直|翻訳|変換|転換|リライト|直す|直し)`),
  kw => new RegExp(`(?:rewrite|write|output|respond|answer)\\s+(?:in|into)\\s+${_escRe(kw)}`, 'i'),
  kw => new RegExp(`MUST\\s+(?:be\\s+in|write\\s+in|use)\\s+${_escRe(kw)}`, 'i'),
  kw => new RegExp(`(?:rewrite|write|translate)\\s*(?:成|為|为|into)?\\s*${_escRe(kw)}`, 'i'),
  kw => new RegExp(`ต้องใช้\\s*${_escRe(kw)}`),
  kw => new RegExp(`เขียนเป็น\\s*${_escRe(kw)}`),
  kw => new RegExp(`ใช้\\s*${_escRe(kw)}\\s*(?:ทั้งหมด|เท่านั้น|ในการ|เขียน|แปล)`),
  kw => new RegExp(`แปล\\s*(?:เป็น|เข้า)\\s*${_escRe(kw)}`),
  kw => new RegExp(`(?:เขียนใหม่|เรียบเรียง|สรุป|แก้ไข|เปลี่ยน|แปลง|ร่าง|พิมพ์)\\s*(?:เป็น|ใน|ด้วย)\\s*${_escRe(kw)}`),
  kw => new RegExp(`เขียน\\s*(?:ด้วย|เป็น)\\s*${_escRe(kw)}`),
  kw => new RegExp(`ใช้\\s*${_escRe(kw)}\\s*(?:เขียน|แปล|สรุป|เรียบเรียง|ร่าง)`),
  kw => new RegExp(`${_escRe(kw)}\\s*(?:로|으로)\\s*(?:작성|번역|쓰기|쓰다|출력|변환|바꾸|고치|리라이트|생성|요약|정리|편집|작문)`),
  kw => new RegExp(`${_escRe(kw)}\\s*(?:를|을)\\s*(?:사용|쓰다|쓰기)`),
  kw => new RegExp(`${_escRe(kw)}\\s*(?:로|으로)\\s*다시\\s*(?:쓰다|쓰기|작성)`),
  kw => new RegExp(`用\\s*${_escRe(kw)}\\s*(?:rewrite|write|translate|generate|改寫|改写|撰寫|撰写|翻譯|翻译)`),
  kw => new RegExp(`${_escRe(kw)}\\s*(?:で|に)\\s*(?:rewrite|write|translate|generate)`),
]

function _detectScriptLanguage(text) {
  let thai = 0, hiraKata = 0, hangul = 0, cjk = 0, latin = 0, viet = 0
  for (const ch of text) {
    const c = ch.codePointAt(0)
    if (c >= 0x0E00 && c <= 0x0E7F) thai++
    else if ((c >= 0x3040 && c <= 0x309F) || (c >= 0x30A0 && c <= 0x30FF)) hiraKata++
    else if ((c >= 0xAC00 && c <= 0xD7AF) || (c >= 0x1100 && c <= 0x11FF) || (c >= 0x3130 && c <= 0x318F)) hangul++
    else if (c >= 0x4E00 && c <= 0x9FFF) cjk++
    else if ((c >= 0x00C0 && c <= 0x024F) || (c >= 0x1E00 && c <= 0x1EFF)) viet++
    else if (c >= 0x0041 && c <= 0x007A) latin++
  }
  const total = thai + hiraKata + hangul + cjk + latin + viet
  if (total === 0) return null
  if (thai > 0 && thai >= hiraKata && thai >= hangul) return "ภาษาไทย"
  if (hiraKata > 0) return "日本語"
  if (hangul > 0 && hangul >= cjk) return "한국어"
  if (cjk > 0 && cjk >= latin) return "繁體中文"
  if (viet > latin * 0.05 && latin > 0) return "Tiếng Việt"
  if (latin > 0) return "English"
  return null
}

const detectedPromptLanguage = computed(() => {
  const ptId = data.article.prompt_template_id
  if (!ptId) return null
  const content = data.prompt_templates_content[ptId]
  if (!content) return null

  // Phase 1: explicit target-language patterns
  for (const entry of _langEntries) {
    for (const kw of entry.keywords) {
      if (_targetPatterns.some(fn => fn(kw).test(content))) return entry.label
    }
  }

  // Phase 2: detect the dominant script of the prompt text itself
  return _detectScriptLanguage(content)
})

onMounted(async () => {

  if( ! await accessControlApi.check_option_access()) {
    await router.push(`/forwriting/options_error`)
    return
  }

  // load article from db
  const resp_0 = await articlesApi.get_article( article_id)
  data.article = resp_0.data
  console.log("** article:", data.article)
  
  // 如果舊文章使用 generate_type_id 1 或 2，自動轉換為 3
  if( data.article.generate_type_id === 1 || data.article.generate_type_id === 2) {
    console.log(`舊文章使用 generate_type_id ${data.article.generate_type_id}，自動轉換為 3`)
    data.article.generate_type_id = 3
  }
  
  // 確保 image_generation_mode 和 image_style_setting 正確加載
  // 如果 API 返回 null 或 undefined，使用默認值
  if( data.article.image_generation_mode === null || data.article.image_generation_mode === undefined) {
    data.article.image_generation_mode = false
  }
  if( data.article.image_style_setting === null || data.article.image_style_setting === undefined) {
    data.article.image_style_setting = ""
  }
  
  // 更新編輯器內容
  if( data.article.content) {
    // 處理圖片 URL，確保可以正常顯示
    // 如果圖片 URL 有 CORS 問題，可以考慮使用代理
    data.editorData = data.article.content
    console.log("📝 [編輯器] 內容已加載，長度:", data.article.content.length)
    // 檢查內容中是否包含圖片
    const imgMatches = data.article.content.match(/<img[^>]+src=["']([^"']+)["'][^>]*>/gi)
    if (imgMatches) {
      console.log("🖼️ [編輯器] 發現", imgMatches.length, "張圖片")
      imgMatches.forEach((img, index) => {
        const srcMatch = img.match(/src=["']([^"']+)["']/i)
        if (srcMatch) {
          console.log(`  圖片 ${index + 1}: ${srcMatch[1]}`)
        }
      })
    }
  }

  // load generate_types from db
  const resp_1 = await articlesApi.get_generate_types()
  for( const i in resp_1.data) {
    const item = resp_1.data[i]
    data.generate_types[ item.id] = item.generate_type_name
  }

  // load ai_models from db
  const resp_ai = await articlesApi.get_ai_models()
  for( const i in resp_ai.data) {
    const item = resp_ai.data[i]
    data.ai_models[ item.id] = item.model_name
  }
  
  // 如果文章沒有 ai_model_id，設置預設值
  if (!data.article.ai_model_id) {
    data.article.ai_model_id = 1  // 預設為 Claude Sonnet 4.5
  }

  // load sample_articles from db
  const resp_2 = await sample_articles_Api.getAll_filter( article_list_id)
  for( const i in resp_2.data) {
    const item = resp_2.data[i]
    data.sample_articles[ item.id] = item.sample_article_name
    data.sample_articles_content[ item.id] = item.sample_article_content
  }

  // load prompt_templates from db
  const resp_3 = await prompt_templates_Api.getAll_filter( article_list_id)
  for( const i in resp_3.data) {
    const item = resp_3.data[i]
    data.prompt_templates[ item.id] = item.prompt_template_name
    data.prompt_templates_content[ item.id] = item.prompt_template_content
  }

  // get futured_media
  await get_futured_media()

  // load futured_medias from db
  const resp_5 = await futured_medias_Api.getAll_filter( article_list_id)
  for( const i in resp_5.data) {
    const item = resp_5.data[i]
    data.futured_medias[ item.id] = item.file_name
  }

  // load connections from db
  const resp_6 = await connectionsApi.getAll_filter( article_list_id)
  for( const i in resp_6.data) {
    const item = resp_6.data[i]
    data.connections[ item.id] = item.connection_name
    // 存儲連接的完整信息（包括 integration）
    data.connections_data[ item.id] = item
  }
  
  data.article.futured_media_id = normalizeFuturedMediaId(data.article.futured_media_id)

  if( data.article.content)
    data.editorData = data.article.content

  if( data.article.tags)
    data.tags = data.article.tags.split( ",")

  // check the point_limit
  point_limit.is_over = await ref_CheckPointLimit.value.check()

  // 生成中状態で表示された場合（新規作成から遷移 など）は自動でポーリング開始
  if (data.article.article_generate_status === 1) {
    console.log("🔄 [onMounted] 生成中のためポーリングを自動開始")
    startPolling()
  }
})

// 左カラム開閉
const sidebarOpen = ref(true)

const tags_changed = async( tags) => {
  data.tags = tags
  console.log( "data.tags:", data.tags)
}

// ===== 共通ポーリング関数 =====
// regenerate_clicked() からも onMounted（生成中状態での初期表示）からも呼び出す
const startPolling = () => {
  const pollInterval = 3000 // 3秒ごとに確認
  const maxAttempts = 120   // 最大120回（6分）
  let attempts = 0

  const pollStatus = async () => {
    try {
      const resp = await articlesApi.get_article(article_id)
      const article = resp.data

      // Object.assign で reactive を維持したまま各プロパティを更新
      Object.assign(data.article, article)

      if (article.content) {
        data.editorData = article.content
      }

      // vue3-tags-input は data.tags を参照するため、DB/API の article.tags を同期する
      if (article.tags) {
        data.tags = article.tags.split(",")
      }

      // 生成完了
      if (article.article_generate_status === 2) {
        console.log("✅ [ポーリング] 文章生成完了")
        return
      }
      // 生成失敗
      if (article.article_generate_status === 3) {
        console.error("❌ [ポーリング] 文章生成失敗")
        return
      }

      // 継続ポーリング
      attempts++
      console.log(`🔄 [ポーリング] 生成中... (${attempts}/${maxAttempts})`)
      if (attempts < maxAttempts) {
        setTimeout(pollStatus, pollInterval)
      } else {
        console.warn("⏰ [ポーリング] タイムアウト")
      }
    } catch (error) {
      console.error("❌ [ポーリング] エラー:", error)
    }
  }

  setTimeout(pollStatus, pollInterval)
}

const regenerate_clicked = async( ) => {
  console.log("=" * 80)
  console.log("🔄 [新產生] 按鈕被點擊")
  console.log("=" * 80)
  console.log("📊 [新產生] 當前狀態:")
  console.log("   - article_id:", article_id)
  console.log("   - point_limit.is_over:", point_limit.is_over)
  console.log("   - data.article:", data.article)
  
  try {
    // 檢查按鈕是否被禁用
    if (point_limit.is_over) {
      console.error("❌ [新產生] 按鈕被禁用（點數不足）")
      alert("點數不足，無法生成文章")
      return
    }
    
    // 如果有必須欄位未填寫
    const has_empty = open_check_modal()
    if( has_empty) {
      console.log("⚠️ [新產生] 有必填欄位未填寫，已顯示提示框")
      return
    }

    console.log("📝 [新產生] 開始更新文章...")
    // 先 update
    await update()
    console.log("✅ [新產生] 文章更新完成")

    console.log("🚀 [新產生] 調用後端 API 生成文章...")
    console.log("   - API URL: /forwriting/articles/" + article_id + "/generate")
    let resp
    try {
      // 調用 backend API
      resp = await articlesApi.generate( article_id)
      console.log("📥 [新產生] 後端 API 回應:", resp)
      console.log("📥 [新產生] 回應狀態碼:", resp.status)
      console.log("📥 [新產生] 回應數據:", resp.data)
    } catch (api_error) {
      console.error("❌ [新產生] API 調用異常:", api_error)
      console.error("   - 錯誤訊息:", api_error.message)
      console.error("   - 錯誤堆疊:", api_error.stack)
      if (api_error.response) {
        console.error("   - 回應狀態碼:", api_error.response.status)
        console.error("   - 回應數據:", api_error.response.data)
        alert(`API 調用失敗：狀態碼 ${api_error.response.status}\n${JSON.stringify(api_error.response.data)}`)
      } else if (api_error.request) {
        console.error("   - 請求已發送但沒有收到回應")
        alert("API 調用失敗：無法連接到後端服務器\n請確認後端正在運行")
      } else {
        alert(`API 調用失敗：${api_error.message}`)
      }
      return
    }
    
    if( resp.status !== 200) {
      console.error("❌ [新產生] API 調用失敗，狀態碼:", resp.status)
      console.error("❌ [新產生] 錯誤詳情:", resp.data)
      data.article.article_generate_status = 3  // エラー即時反映
      alert(`文章生成失敗：狀態碼 ${resp.status}\n${JSON.stringify(resp.data)}`)
      return
    }

    // ✅ API成功直後にステータスを「生成中(1)」へ即時反映
    data.article.article_generate_status = 1
    console.log("✅ [新產生] API 調用成功、ステータスを生成中(1)に即時更新")

    // ポーリング開始
    startPolling()
  } catch (error) {
    console.error("❌ [新產生] 發生未預期的錯誤:", error)
    console.error("❌ [新產生] 錯誤堆疊:", error.stack)
    alert(`文章生成過程中發生錯誤: ${error.message}\n請檢查瀏覽器控制台獲取詳細信息`)
  }
}

const publish_clicked = async( ) => {
  console.log( "publish_clicked()...")
  console.log( "article_id:", article_id)
  isLoading.show = true

  try {
    // 先 update
    await update()

    // 調用 backend API with connection_id
    const connectionId = data.article.connection_id || 0
    console.log("*** connectionId:", connectionId)
    const resp = await articlesApi.post( article_id, connectionId)
    console.log("*** resp from articlesApi.post:", resp)

    if( resp.status !== 200) {
      throw new Error("articlesApi.post() ERROR");
    }

    isLoading.show = false

    // == 切換到 [5] ==
    await router.push(`/forwriting/article_lists/${article_list_id}/articles`)
  } catch (error) {
    isLoading.show = false
    console.error("❌ [發布草稿] 發生錯誤:", error)
    console.error("❌ [發布草稿] 錯誤詳情:", error.response?.data || error.message)
    alert(`發布失敗: ${error.response?.data?.detail || error.message}\n請檢查瀏覽器控制台獲取詳細信息`)
  }
}

// button update
const update = async( ) => {
  data.article.tags    = data.tags.toString()
  data.article.content = data.editorData

  const articleData = { ...data.article }
  articleData.futured_media_id = normalizeFuturedMediaId(articleData.futured_media_id)

  console.log( "*** data.article(update):", articleData)

  isLoading.show = true;

  const resp = await articlesApi.update( article_id, articleData)
  if( resp.status !== 200) {
    isLoading.show = false;
    throw new Error("articlesApi.update() ERROR");
  }

  isLoading.show = false;
}

// button return
// const return_clicked = async( e) => {
//   await router.push(`/forwriting/article_lists/${article_list_id}/articles`);
// }

// button return to list
const return_to_list = async( e) => {
  await router.push(`/forwriting/article_lists/${article_list_id}/articles`);
}

// is_loading
const isLoading = reactive({
  show: false
})

// ==== modal_over_points ====
const ref_CheckPointLimit = ref(null)
const point_limit = reactive({
  is_over: false,
})

// ==== 生成/投稿ステータス表示 ====
const generateStatusConfig = computed(() => {
  const s = data.article.article_generate_status
  if (s === 1) return { label: t('Articles.article_generate_status_1'), color: '#0d6efd', bg: '#e8f0fe', spinning: true  }
  if (s === 2) return { label: t('Articles.article_generate_status_2'), color: '#198754', bg: '#d1f0e0', spinning: false }
  if (s === 3) return { label: t('Articles.article_generate_status_3'), color: '#dc3545', bg: '#fde8ea', spinning: false }
  return       { label: t('Articles.article_generate_status_0'), color: '#6c757d', bg: '#f0f0f0', spinning: false }
})

const postStatusConfig = computed(() => {
  const s = data.article.cms_post_status
  if (s === 1) return { label: t('Articles.cms_post_status_1'), color: '#0d6efd', bg: '#e8f0fe', spinning: true  }
  if (s === 2) return { label: t('Articles.cms_post_status_2'), color: '#198754', bg: '#d1f0e0', spinning: false }
  if (s === 3) return { label: t('Articles.cms_post_status_3'), color: '#dc3545', bg: '#fde8ea', spinning: false }
  return       { label: t('Articles.cms_post_status_0'), color: '#6c757d', bg: '#f0f0f0', spinning: false }
})

</script>

<!--just for CKEditor-->
<script>
  // import { defineComponent } from 'vue';
  import Vue3TagsInput from 'vue3-tags-input';

  // for CKEditor
  // npm install --save @ckeditor/ckeditor5-vue @ckeditor/ckeditor5-build-classic
  import {
    ClassicEditor,
    AccessibilityHelp,
    Autoformat,
    AutoImage,
    Autosave,
    BlockQuote,
    Bold,
    CloudServices,
    Essentials,
    GeneralHtmlSupport,
    Heading,
    ImageBlock,
    ImageCaption,
    ImageInline,
    ImageInsertViaUrl,
    ImageResize,
    ImageStyle,
    ImageTextAlternative,
    ImageToolbar,
    ImageUpload,
    Indent,
    IndentBlock,
    Italic,
    Link,
    LinkImage,
    List,
    ListProperties,
    MediaEmbed,
    Paragraph,
    PasteFromOffice,
    SelectAll,
    SourceEditing,
    Table,
    TableCaption,
    TableCellProperties,
    TableColumnResize,
    TableProperties,
    TableToolbar,
    TextTransformation,
    TodoList,
    Underline,
    Undo
  } from 'ckeditor5';

  import 'ckeditor5/ckeditor5.css';

  export default {
    name: 'app',
    components: {
      Vue3TagsInput
    },
    data() {
      return {
        // tags: ['VUE', 'HTML', 'CSS'],
        isLayoutReady: false,
        config: null, // CKEditor needs the DOM tree before calculating the configuration.
        editor: ClassicEditor
      };
    },
    mounted() {
      this.config = {
        toolbar: {
          items: [
            'undo',
            'redo',
            '|',
            'heading',
            '|',
            'bold',
            'italic',
            'underline',
            '|',
            'link',
            'mediaEmbed',
            'insertTable',
            'blockQuote',
            '|',
            'bulletedList',
            'numberedList',
            'todoList',
            'outdent',
            'indent',
            '|',
            'sourceEditing'
          ],
          shouldNotGroupWhenFull: false
        },
        licenseKey: 'GPL',
        plugins: [
          AccessibilityHelp,
          Autoformat,
          AutoImage,
          Autosave,
          BlockQuote,
          Bold,
          CloudServices,
          Essentials,
          GeneralHtmlSupport,
          Heading,
          ImageBlock,
          ImageCaption,
          ImageInline,
          ImageInsertViaUrl,
          ImageResize,
          ImageStyle,
          ImageTextAlternative,
          ImageToolbar,
          ImageUpload,
          Indent,
          IndentBlock,
          Italic,
          Link,
          LinkImage,
          List,
          ListProperties,
          MediaEmbed,
          Paragraph,
          PasteFromOffice,
          SelectAll,
          SourceEditing,
          Table,
          TableCaption,
          TableCellProperties,
          TableColumnResize,
          TableProperties,
          TableToolbar,
          TextTransformation,
          TodoList,
          Underline,
          Undo
        ],
        heading: {
          options: [
            {
              model: 'paragraph',
              title: 'Paragraph',
              class: 'ck-heading_paragraph'
            },
            {
              model: 'heading1',
              view: 'h1',
              title: 'Heading 1',
              class: 'ck-heading_heading1'
            },
            {
              model: 'heading2',
              view: 'h2',
              title: 'Heading 2',
              class: 'ck-heading_heading2'
            },
            {
              model: 'heading3',
              view: 'h3',
              title: 'Heading 3',
              class: 'ck-heading_heading3'
            },
            {
              model: 'heading4',
              view: 'h4',
              title: 'Heading 4',
              class: 'ck-heading_heading4'
            },
            {
              model: 'heading5',
              view: 'h5',
              title: 'Heading 5',
              class: 'ck-heading_heading5'
            },
            {
              model: 'heading6',
              view: 'h6',
              title: 'Heading 6',
              class: 'ck-heading_heading6'
            }
          ]
        },
        image: {
          toolbar: [
            'toggleImageCaption',
            'imageTextAlternative',
            '|',
            'imageStyle:inline',
            'imageStyle:wrapText',
            'imageStyle:breakText',
            '|',
            'resizeImage'
          ],
          // 允許外部圖片 URL
          upload: {
            types: ['png', 'jpg', 'jpeg', 'gif', 'bmp', 'webp', 'svg']
          }
        },
        // initialData:
        //     '<h2>Congratulations on setting up CKEditor 5! 🎉</h2>\n<p>\n    You\'ve successfully created a CKEditor 5 project. This powerful text editor will enhance your application, enabling rich text editing\n    capabilities that are customizable and easy to use.\n</p>\n<h3>What\'s next?</h3>\n<ol>\n    <li>\n        <strong>Integrate into your app</strong>: time to bring the editing into your application. Take the code you created and add to your\n        application.\n    </li>\n    <li>\n        <strong>Explore features:</strong> Experiment with different plugins and toolbar options to discover what works best for your needs.\n    </li>\n    <li>\n        <strong>Customize your editor:</strong> Tailor the editor\'s configuration to match your application\'s style and requirements. Or even\n        write your plugin!\n    </li>\n</ol>\n<p>\n    Keep experimenting, and don\'t hesitate to push the boundaries of what you can achieve with CKEditor 5. Your feedback is invaluable to us\n    as we strive to improve and evolve. Happy editing!\n</p>\n<h3>Helpful resources</h3>\n<ul>\n    <li>📝 <a href="https://orders.ckeditor.com/trial/premium-features">Trial sign up</a>,</li>\n    <li>📕 <a href="https://ckeditor.com/docs/ckeditor5/latest/installation/index.html">Documentation</a>,</li>\n    <li>⭐️ <a href="https://github.com/ckeditor/ckeditor5">GitHub</a> (star us if you can!),</li>\n    <li>🏠 <a href="https://ckeditor.com">CKEditor Homepage</a>,</li>\n    <li>🧑‍💻 <a href="https://ckeditor.com/ckeditor-5/demo/">CKEditor 5 Demos</a>,</li>\n</ul>\n<h3>Need help?</h3>\n<p>\n    See this text, but the editor is not starting up? Check the browser\'s console for clues and guidance. It may be related to an incorrect\n    license key if you use premium features or another feature-related requirement. If you cannot make it work, file a GitHub issue, and we\n    will help as soon as possible!\n</p>\n',
        link: {
          addTargetToExternalLinks: true,
          defaultProtocol: 'https://',
          decorators: {
            toggleDownloadable: {
              mode: 'manual',
              label: 'Downloadable',
              attributes: {
                download: 'file'
              }
            }
          }
        },
        list: {
          properties: {
            styles: true,
            startIndex: true,
            reversed: true
          }
        },
        placeholder: 'Type or paste your content here!',
        htmlSupport: {
          allow: [
            {
              name: /^.*$/,
              attributes: true,
              classes: true,
              styles: true
            }
          ]
        },
        table: {
          contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells', 'tableProperties', 'tableCellProperties']
        }
      };

      this.isLayoutReady = true;
    }
  };
</script>

<template>
  <LoadingIcon :is-loading="isLoading.show"></LoadingIcon>

  <!-- modal_over_points -->
  <CheckPointLimit ref="ref_CheckPointLimit"></CheckPointLimit>

  <!-- article_check_modal" -->
  <div v-if="modal_check.show" class="modal-del">
    <div class="modal-content">

      <div class="row" style="margin-top: 20px;text-align: center">
        <p>{{ modal_check.msg }}</p>
      </div>

      <!-- 1 buttons -->
      <div class="row" style="margin-top: 20px;margin-left: 10px; margin-right: 10px">
        <button class="btn btn-primary"
                @click="modal_check.show=false">
          {{ $t( "Articles.OK") }}</button>
      </div>
    </div>
  </div>
  <!-- end of article_check_modal" -->

  <div class="edit-page">
    <div class="edit-layout">

      <!-- ===== 左サイドバー: 生成条件 ===== -->
      <aside class="sidebar" :class="{ 'sidebar-closed': !sidebarOpen }">

        <!-- サイドバーヘッダー -->
        <div class="sidebar-header" @click="sidebarOpen = !sidebarOpen">
          <h4 class="sidebar-title">{{ $t( "Articles_Edit.h4") }}</h4>
          <span class="sidebar-toggle" :class="{ 'toggle-closed': !sidebarOpen }">&#9662;</span>
        </div>

        <!-- サイドバー中身 -->
        <div class="sidebar-body" v-show="sidebarOpen">

          <!-- generate_type -->
          <div class="edit-field">
            <label class="edit-label required">{{ $t( "Articles_Edit.generate_type") }}</label>
            <select class="form-select form-select-sm"
                    @change="generate_type_changed"
                    v-model="data.article.generate_type_id">
              <option value=3>{{ $t( "Articles.generate_type_3") }}</option>
              <option value=4>{{ $t( "Articles.generate_type_4") }}</option>
              <option value=5>{{ $t( "Articles.generate_type_5") }}</option>
              <option value=6>{{ $t( "Articles.generate_type_6") }}</option>
            </select>
          </div>

          <!-- AIモデル -->
          <div class="edit-field">
            <label class="edit-label required">{{ $t( "Articles_Edit.ai_model") }}</label>
            <select class="form-select form-select-sm"
                    v-model="data.article.ai_model_id">
              <option :value="id" v-for="(name, id) in data.ai_models" :key="id">
                {{ name }}
              </option>
            </select>
          </div>

          <!-- 画像生成モード -->
          <div class="edit-field">
            <label class="edit-label">{{ $t( "Articles_Edit.image_generation_mode") }}</label>
            <div style="display: flex; align-items: center; gap: 8px; flex-wrap: wrap;">
              <label class="switch">
                <input type="checkbox"
                       v-model="data.article.image_generation_mode"
                       :disabled="parseInt(data.article.generate_type_id) === 4">
                <span class="slider round"></span>
              </label>
              <select v-if="data.article.image_generation_mode && parseInt(data.article.generate_type_id) !== 4"
                      class="form-select form-select-sm"
                      v-model="data.article.image_style_setting"
                      style="width: 120px;">
                <option value="">{{ $t( "Articles_Edit.select_image_style") }}</option>
                <option value="realistic">{{ $t( "Articles_Edit.image_style_realistic") }}</option>
                <option value="anime">{{ $t( "Articles_Edit.image_style_anime") }}</option>
                <option value="illustration">{{ $t( "Articles_Edit.image_style_illustration") }}</option>
                <option value="photography">{{ $t( "Articles_Edit.image_style_photography") }}</option>
              </select>
              <span v-if="parseInt(data.article.generate_type_id) === 4"
                    style="color: #888; font-size: 11px;">
                (画像転載モードでは無効)
              </span>
            </div>
          </div>

          <hr class="sidebar-divider">

          <!-- [ 5]: audio file info (edit screen - read only display) -->
          <div class="edit-field" v-show="parseInt(data.article.generate_type_id) === 5">
            <label class="edit-label">{{ $t("Articles.audio_file") }}</label>
            <div style="font-size: 12px; color: #555;">
              <span v-if="data.article.audio_blob_path">
                ✅ {{ data.article.audio_blob_path.split('/').pop() }}
              </span>
              <span v-else style="color: #aaa;">{{ $t("Articles.audio_no_file") }}</span>
            </div>
          </div>

          <!-- [ 5]: audio supplement (edit screen) -->
          <div class="edit-field" v-show="parseInt(data.article.generate_type_id) === 5">
            <label class="edit-label">{{ $t("Articles.audio_supplement") }}</label>
            <textarea rows="3" class="form-control form-control-sm"
                      :placeholder="$t('Articles.audio_supplement_placeholder')"
                      v-model="data.article.audio_supplement"></textarea>
          </div>

          <!-- インライン画像表示 -->
          <div class="edit-field" v-if="data.article.inline_image_blob_paths && parseInt(data.article.generate_type_id) !== 4">
            <label class="edit-label">{{ $t("Articles.inline_images") }}</label>
            <div v-for="(path, idx) in (JSON.parse(data.article.inline_image_blob_paths || '[]'))" :key="idx"
                 style="font-size: 12px; color: #555; margin-bottom: 2px;">
              📷 {{ path.split('/').pop() }}
            </div>
          </div>

          <!-- urls -->
          <div class="edit-field" v-show="parseInt(data.article.generate_type_id) !== 5 && parseInt(data.article.generate_type_id) !== 6">
            <div style="display: flex; align-items: center; gap: 6px; flex-wrap: wrap;">
              <label class="edit-label" style="margin-bottom: 0;">{{ $t( "Articles_Edit.urls") }}</label>
              <label v-show="[ 3, 4].includes( parseInt(data.article.generate_type_id))"
                     class="required" style="margin-bottom: 0;"></label>
              <!-- URL/テキスト モードバッジ -->
              <span v-show="[ 3, 4].includes( parseInt(data.article.generate_type_id)) && data.article.urls"
                    class="mode-badge"
                    :class="isUrlMode ? 'mode-url' : 'mode-text'">
                {{ isUrlMode ? 'URL' : 'テキスト' }}
              </span>
              <button v-show="[ 3, 4].includes( parseInt(data.article.generate_type_id)) && isUrlMode"
                      class="btn btn-outline-secondary btn-sm"
                      style="font-size: 10px; padding: 1px 6px; line-height: 1.4;"
                      @click="check_primary_source"
                      :disabled="primarySourceCheck.loading || !data.article.urls">
                <span v-if="primarySourceCheck.loading">{{ $t('Articles_Edit.primary_source_checking') }}</span>
                <span v-else>{{ $t('Articles_Edit.primary_source_check') }}</span>
              </button>
            </div>
            <textarea rows="5" class="form-control form-control-sm"
                :placeholder="$t('Articles_Edit.urls_placeholder')"
                v-model="data.article.urls" ></textarea>
            <div class="urls-hint">{{ $t('Articles_Edit.urls_hint') }}</div>
            <!-- 一次ソース判定結果 -->
            <div v-if="primarySourceCheck.checked" style="margin-top: 6px;">
              <div v-for="(result, index) in primarySourceCheck.results" :key="index"
                   class="primary-source-result"
                   :class="{
                     'result-primary': result.is_primary_source === true,
                     'result-secondary': result.is_primary_source === false,
                     'result-unknown': result.is_primary_source === null
                   }">
                <div style="display: flex; align-items: center; gap: 4px; flex-wrap: wrap;">
                  <span class="badge"
                        :class="{
                          'bg-success': result.is_primary_source === true,
                          'bg-warning text-dark': result.is_primary_source === false,
                          'bg-secondary': result.is_primary_source === null
                        }"
                        style="font-size: 10px;">
                    {{ result.is_primary_source === true
                       ? $t('Articles_Edit.primary_source_yes')
                       : result.is_primary_source === false
                         ? $t('Articles_Edit.primary_source_no')
                         : $t('Articles_Edit.primary_source_unknown') }}
                  </span>
                  <span v-if="result.confidence > 0" style="font-size: 10px; color: #666;">
                    {{ result.confidence }}%
                  </span>
                </div>
                <div style="font-size: 11px; color: #555; margin-top: 2px;">
                  {{ result.reason }}
                </div>
              </div>
            </div>
          </div>

          <hr class="sidebar-divider">

          <!-- sample_article -->
          <div class="edit-field">
            <label class="edit-label" :class="parseInt(data.article.generate_type_id) !== 6 ? 'required' : ''">{{ $t( "Articles_Edit.sample_article_name") }}</label>
            <select class="form-select form-select-sm"
                    v-model="data.article.sample_article_id">
              <option v-for="(item, index) in data.sample_articles"
                      :value="index" :key="index">
                {{item}}</option>
            </select>
          </div>

          <!-- prompt_template -->
          <div class="edit-field">
            <label class="edit-label" :class="parseInt(data.article.generate_type_id) !== 6 ? 'required' : ''">{{ $t( "Articles_Edit.prompt_template_name") }}</label>
            <div class="d-flex align-items-center gap-2">
              <select class="form-select form-select-sm"
                      v-model="data.article.prompt_template_id">
                <option v-for="(item, index) in data.prompt_templates"
                        :value="index" :key="index">
                  {{item}}</option>
              </select>
              <span v-if="detectedPromptLanguage"
                    class="badge bg-info text-dark text-nowrap"
                    style="font-size: 0.78rem;">
                {{ detectedPromptLanguage }}
              </span>
            </div>
          </div>

          <!-- additional_prompt / AI命令 -->
          <div class="edit-field">
            <label class="edit-label" :class="parseInt(data.article.generate_type_id) === 6 ? 'required' : ''">
              {{ parseInt(data.article.generate_type_id) === 6
                ? $t("Articles_Edit.agent_instruction")
                : $t("Articles_Edit.additional_prompt") }}
            </label>
            <textarea class="form-control form-control-sm" rows="4"
                      :placeholder="parseInt(data.article.generate_type_id) === 6
                        ? $t('Articles_Edit.agent_instruction_placeholder')
                        : $t('Articles_Edit.additional_prompt_placeholder')"
                      v-model="data.article.additional_prompt"></textarea>
          </div>

          <!-- connection -->
          <div class="edit-field">
            <label class="edit-label">{{ $t( "Articles_Edit.connection") }}</label>
            <select class="form-select form-select-sm"
                    v-model="data.article.connection_id">
              <option :value="null">{{ $t("Articles.none") }}</option>
              <option :value="parseInt(index)" v-for="(item, index) in data.connections" :key="index">
                {{item}}</option>
            </select>
          </div>

          <!-- futured_media -->
          <div class="edit-field">
            <label class="edit-label">{{ $t( "Articles_Edit.futured_media") }}</label>
            <select class="form-select form-select-sm"
                    @change="get_futured_media"
                    v-model="data.article.futured_media_id">
              <option  value=0> - </option>
              <option v-if="isWordPressConnection" value="-1">{{ $t("Articles.ai_generation") }}</option>
              <option :value="index" v-for="(item, index) in data.futured_medias" :key="index">
                {{item}}</option>
            </select>
            <img v-if="data.futured_media.blob_storage_path"
                 :src="data.futured_media.blob_storage_path+'?t='+Date.now()"
                 alt="Dropped Image"
                 style="max-width: 100%; height: auto; margin-top: 6px; border-radius: 4px;" />
          </div>

          <!-- buttons -->
          <div style="display: flex; gap: 8px; margin-top: 14px; padding-bottom: 4px; flex-wrap: wrap; align-items: center;">
            <button class="btn btn-success btn-sm"
                    @click="regenerate_clicked"
                    :disabled="point_limit.is_over || data.article.article_generate_status === 1">
              <span v-if="data.article.article_generate_status === 1"
                    class="status-spinner status-spinner--sm" style="margin-right: 4px;"></span>
              {{ data.article.article_generate_status === 1
                  ? $t('Articles.article_generate_status_1')
                  : $t('Articles_Edit.regenerate') }}
            </button>

            <button class="btn btn-outline-secondary btn-sm"
                    @click="return_to_list">
              {{ $t( "Articles_Edit.return_to_list") }}</button>
          </div>

        </div>
      </aside>

      <!-- ===== 右メイン: 生成結果 ===== -->
      <main class="main-content">

        <h4 class="main-title">{{ $t( "Articles_Edit.generate_result") }}</h4>

        <!-- ===== ステータスバー ===== -->
        <div class="status-bar">
          <!-- 生成ステータス -->
          <div class="status-card"
               :style="{ borderColor: generateStatusConfig.color, background: generateStatusConfig.bg }">
            <span class="status-label" :style="{ color: generateStatusConfig.color }">
              {{ $t('Articles.article_generate_status') }}
            </span>
            <div class="status-value">
              <span v-if="generateStatusConfig.spinning" class="status-spinner"></span>
              <span class="status-text" :style="{ color: generateStatusConfig.color }">
                {{ generateStatusConfig.label }}
              </span>
            </div>
            <!-- 生成中プログレスバー -->
            <div v-if="data.article.article_generate_status === 1" class="status-progress">
              <div class="status-progress-bar"></div>
            </div>
          </div>

          <!-- 投稿ステータス -->
          <div class="status-card"
               :style="{ borderColor: postStatusConfig.color, background: postStatusConfig.bg }">
            <span class="status-label" :style="{ color: postStatusConfig.color }">
              {{ $t('Articles.cms_post_status') }}
            </span>
            <div class="status-value">
              <span v-if="postStatusConfig.spinning" class="status-spinner"></span>
              <span class="status-text" :style="{ color: postStatusConfig.color }">
                {{ postStatusConfig.label }}
              </span>
            </div>
          </div>
        </div>
        <!-- ===== /ステータスバー ===== -->

        <!-- posted_url -->
        <div class="edit-field">
          <label class="edit-label">{{ $t( "Articles_Edit.posted_url") }}</label>
          <div>
            <a v-if="data.article.posted_url" :href="data.article.posted_url" target="_blank" class="text-primary text-decoration-none" style="font-size: 13px;">
              {{data.article.posted_url}}
            </a>
            <span v-else style="color: #aaa;">-</span>
          </div>
        </div>

        <!-- slug -->
        <div class="edit-field">
          <label class="edit-label">{{ $t( "Articles_Edit.slug") }}</label>
          <input type="text" class="form-control form-control-sm"
                 v-model="data.article.slug"/>
        </div>

        <!-- article_title -->
        <div class="edit-field">
          <label class="edit-label">{{ $t( "Articles_Edit.article_title") }}</label>
          <input type="text" class="form-control"
                 v-model="data.article.article_title"/>
        </div>

        <!-- content( by ckeditor5-vue) -->
        <div class="edit-field">
          <label class="edit-label">{{ $t( "Articles_Edit.content") }}</label>
          <div ref="editorElement">
            <ckeditor
                v-if="isLayoutReady"
                v-model="data.editorData"
                :editor="editor" :config="config" />
          </div>
        </div>

        <!-- tags -->
        <div class="edit-field">
          <label class="edit-label">{{ $t( "Articles_Edit.tags") }}</label>
          <vue3-tags-input :tags="data.tags"
                           style="width: 100%"
                           @on-tags-changed="tags_changed"/>
        </div>

        <!-- action buttons -->
        <div style="display: flex; align-items: center; gap: 10px; margin-top: 16px; padding-bottom: 20px;">
          <button class="btn btn-success"
                  style="min-width: 160px;"
                  @click="publish_clicked">
            {{ $t( "Articles_Edit.publish") }}</button>

          <button class="btn btn-primary"
                  v-show="data.article.generate_type_id > 0"
                  @click="update">
            {{ $t( "Articles_Edit.update") }}</button>

          <button class="btn btn-outline-secondary"
                  @click="return_to_list">
            {{ $t( "Articles_Edit.return_to_list") }}</button>
        </div>

      </main>

    </div>
  </div>
</template>

<style scoped>

/* === ページ全体 === */
.edit-page {
  padding: 0 8px;
}

/* === 2カラムレイアウト === */
.edit-layout {
  display: flex;
  gap: 20px;
  align-items: flex-start;
}

/* === 左サイドバー === */
.sidebar {
  flex: 0 0 280px;
  min-width: 0;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  transition: flex-basis 0.2s ease;
  position: sticky;
  top: 10px;
}

.sidebar-closed {
  flex: 0 0 auto;
}

.sidebar-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  cursor: pointer;
  user-select: none;
  border-bottom: 1px solid #e9ecef;
}

.sidebar-closed .sidebar-header {
  border-bottom: none;
}

.sidebar-title {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin: 0;
}

.sidebar-toggle {
  font-size: 12px;
  color: #888;
  transition: transform 0.2s ease;
}

.toggle-closed {
  transform: rotate(-90deg);
}

.sidebar-body {
  padding: 12px 14px;
}

.sidebar-divider {
  margin: 8px 0;
  border-color: #dee2e6;
  opacity: 0.5;
}

/* === 右メインエリア === */
.main-content {
  flex: 1;
  min-width: 0;
}

.main-title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 2px solid #e9ecef;
}

/* === フォーム共通 === */
.edit-field {
  margin-bottom: 10px;
}

.edit-label {
  display: block;
  font-size: 12px;
  font-weight: 500;
  color: #666;
  margin-bottom: 3px;
}

.sidebar .form-select,
.sidebar .form-control,
.sidebar textarea {
  width: 100%;
}

.main-content .form-control,
.main-content textarea {
  width: 100%;
}

/* === ステータスバー === */
.status-bar {
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}

.status-card {
  flex: 1;
  min-width: 120px;
  border: 1.5px solid #ccc;
  border-radius: 8px;
  padding: 8px 12px 6px;
  position: relative;
  overflow: hidden;
}

.status-label {
  display: block;
  font-size: 10px;
  font-weight: 600;
  opacity: 0.8;
  margin-bottom: 4px;
  letter-spacing: 0.03em;
}

.status-value {
  display: flex;
  align-items: center;
  gap: 6px;
}

.status-text {
  font-size: 15px;
  font-weight: 700;
}

/* スピナー */
.status-spinner {
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid currentColor;
  border-top-color: transparent;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  flex-shrink: 0;
}
.status-spinner--sm {
  width: 11px;
  height: 11px;
  border-width: 2px;
}
@keyframes spin {
  to { transform: rotate(360deg); }
}

/* 生成中プログレスバー */
.status-progress {
  height: 3px;
  background: rgba(0,0,0,0.08);
  border-radius: 2px;
  margin-top: 6px;
  overflow: hidden;
}
.status-progress-bar {
  height: 100%;
  width: 40%;
  background: #0d6efd;
  border-radius: 2px;
  animation: progress-slide 1.4s ease-in-out infinite;
}
@keyframes progress-slide {
  0%   { transform: translateX(-100%); }
  100% { transform: translateX(350%); }
}

/* === URL/テキスト モードバッジ === */
.mode-badge {
  font-size: 10px;
  padding: 1px 7px;
  border-radius: 10px;
  color: #fff;
  font-weight: 600;
  white-space: nowrap;
}
.mode-url  { background: #0d6efd; }
.mode-text { background: #198754; }

.urls-hint {
  font-size: 10px;
  color: #999;
  margin-top: 3px;
}

/* === 一次ソース判定結果 === */
.primary-source-result {
  padding: 6px 10px;
  margin-bottom: 4px;
  border-radius: 4px;
  border-left: 3px solid #ccc;
  font-size: 12px;
}
.result-primary {
  background-color: #f0faf0;
  border-left-color: #28a745;
}
.result-secondary {
  background-color: #fffbf0;
  border-left-color: #ffc107;
}
.result-unknown {
  background-color: #f8f9fa;
  border-left-color: #6c757d;
}

/* === モーダル === */
.modal-del {
  height: 800px;
  max-height: 1024px;
  width: 480px;
  display: flex;
  justify-content: center;
  align-items: center;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0);
  z-index: 1000;
  top: 100px;
  left: 100px;
  position: absolute;
  padding: 20px;
}

.modal-content {
  background-color: #fff;
  padding: 20px;
  border-radius: 5px;
  width: 80%;
  max-width: 500px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
}

.item_title {
  font-weight: bold;
}

.item_label {
  margin-left: 10px;
  margin-top: 20px;
}

.item_content {
  margin-left: 22px;
}

.btn-animation:hover {
  cursor: pointer;
}

.tags {
  position: relative;
  float: left;
  color: #000000;
  line-height: 28px;
  margin-left: 0;
  margin-top: 10px;
  margin-bottom: 1px;
  padding: 0 22px 0 10px;
}

.tags .content {
  line-height: 28px;
}

.tags .del {
  width: 22px;
  height: 28px;
  text-align: center;
  cursor: pointer;
  position: absolute;
  top: -1px;
  right: 0;
}

.tags {
  background-color: #e0e0e0;
  border: 1px solid rgb(54, 54, 54);
  cursor: default;
}

/* 画像生成モード開關樣式 */
.switch {
  position: relative;
  display: inline-block;
  width: 50px;
  height: 24px;
}

.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  transition: .4s;
}

.slider:before {
  position: absolute;
  content: "";
  height: 18px;
  width: 18px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  transition: .4s;
}

input:checked + .slider {
  background-color: #2196F3;
}

input:checked + .slider:before {
  transform: translateX(26px);
}

.slider.round {
  border-radius: 24px;
}

.slider.round:before {
  border-radius: 50%;
}

/* 確保 CKEditor 中的圖片正確顯示 */
:deep(.ck-content) img {
  max-width: 100%;
  height: auto;
  display: block;
}

:deep(.ck-content) figure {
  margin: 1em 0;
}

:deep(.ck-content) figure img {
  width: 100%;
  height: auto;
}

:deep(.ck-content) figcaption {
  text-align: center;
  font-style: italic;
  color: #666;
  margin-top: 0.5em;
}
</style>
